import { useEffect } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Collapse, Space, Switch, Typography } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import type { GaugeComponentSpec, MetricOption, SubjectEntity, ComponentSpec } from "../../Processing/types";
import type { GaugeScaleConfig, GaugeSegmentConfig } from "../../Processing/types";
import { MetricPicker } from "../shared/MetricPicker";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import {
  CountIncludePicker,
  countSourceFromMetricId,
  defaultCountSpec,
} from "../shared/CountIncludePicker";
import { PlayersForm } from "../shared/PlayersForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DateRangeForm } from "../shared/DateRangeForm";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { GaugeScaleForm } from "./GaugeScaleForm";
import { GaugeSegmentForm } from "./GaugeSegmentForm";
import { GaugePreviewPanel } from "./GaugePreviewPanel";
import { isSectionBindable } from "../../Processing/controllerBindings";
import {
  segmentConfigForSource,
  createInitialState,
  formStateToSpec,
  hydrateFromSpec,
  type GaugeFormState,
} from "./gaugeState";

type Action = { type: "SET_FIELD"; field: keyof GaugeFormState; value: unknown };

function reducer(state: GaugeFormState, action: Action): GaugeFormState {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
}


export function GaugeConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<ComponentSpec>) {
  const gaugeSpec = existingSpec?.type === "gauge" ? (existingSpec as GaugeComponentSpec) : undefined;

  const [state, dispatch] = useConfiguratorSync({
    reducer,
    existingSpec: gaugeSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });


  const countSource = countSourceFromMetricId(state.selectedMetricId);
  const set = (field: keyof GaugeFormState) => (value: unknown) =>
    dispatch({ type: "SET_FIELD", field, value });

  const isGpsMetric = (state.selectedMetricId ?? "").startsWith("gps-");
  // A helmet count has no best, so its `*_best` scales fail on save.
  const allowedAggs = metrics.find((m) => m.id === state.selectedMetricId)?.aggregations;
  const hasBest = !allowedAggs || allowedAggs.includes("best");

  const hasSinglePlayer =
    state.selectedSubjects.length === 1 && state.selectedSubjects[0].startsWith("player-");
  const hasPosition = state.selectedSubjects.some((s) => s.startsWith("pos-"));

  // New widgets: pre-select the first available metric.
  useEffect(() => {
    if (gaugeSpec) return;
    if (state.selectedMetricId == null && metrics.length > 0) {
      set("selectedMetricId")(metrics[0].id);
    }
  }, [gaugeSpec, state.selectedMetricId, metrics]);

  // Title auto-follows the selected metric until the user edits it manually.
  useEffect(() => {
    if (state.titleDirty) return;
    const name = metrics.find((m) => m.id === state.selectedMetricId)?.name ?? "";
    if (name !== state.title) set("title")(name);
  }, [state.selectedMetricId, state.titleDirty, metrics]);

  // If the chosen scale type becomes invalid for the current subject/metric, reset to
  // Team Best, or to Team Average for a metric with no best.
  useEffect(() => {
    const t = state.scale.type;
    const disabled =
      ((t === "player_avg" || t === "player_best" || t === "player_sum") && !hasSinglePlayer) ||
      ((t === "position_avg" || t === "position_best" || t === "position_sum") &&
        !hasPosition &&
        !hasSinglePlayer) ||
      ((t === "player_best" || t === "position_best" || t === "team_best") && !hasBest) ||
      (t === "baseline_multiplier" && !isGpsMetric);
    if (disabled) {
      set("scale")({ ...state.scale, type: hasBest ? "team_best" : "team_avg", multiplier: 1.0, customMax: null });
    }
  }, [hasSinglePlayer, hasPosition, isGpsMetric, hasBest, state.scale]);

  const dateFormValue: DateRangeFormFields = {
    dateControllerId: state.dateControllerId,
    dateRange: state.dateRange,
    showAdvancedDate: state.showAdvancedDate,
    startDateType: state.startDateType,
    startDateAbsolute: state.startDateAbsolute,
    startDateOffset: state.startDateOffset,
    startDateLatestN: state.startDateLatestN,
    endDateType: state.endDateType,
    endDateAbsolute: state.endDateAbsolute,
    endDateOffset: state.endDateOffset,
    dayFilter: state.dayFilter,
  };

  const playersFormValue: PlayersFormFields = {
    subjectControllerId: state.subjectControllerId,
    selectedSubjects: state.selectedSubjects,
    selectedDrills: state.selectedDrills,
    subjectActiveTab: state.subjectActiveTab,
  };

  const { Panel } = Collapse;
  const { Text } = Typography;

  const form = (
    <>
      {/* Gauge-level configuration */}
      <Collapse defaultActiveKey={["gauge-config"]} style={{ borderRadius: 0 }}>
        <Panel
          key="gauge-config"
          header={
            <Space size={6}>
              <SettingOutlined />
              <Text strong style={{ fontSize: 13 }}>Gauge Configuration</Text>
            </Space>
          }
        >
          <Space size={8} align="center">
            <Text style={{ fontSize: 12, minWidth: 56 }}>Title</Text>
            <SlicerTokenInput
              size="small"
              placeholder="Gauge title (optional)"
              value={state.title}
              onChange={(title) => {
                if (!state.titleDirty) set("titleDirty")(true);
                set("title")(title);
              }}
            />
          </Space>
        </Panel>
      </Collapse>

      {/* Numbered configuration steps */}
      <Collapse
        accordion
        defaultActiveKey={["1"]}
        className="awm-inner-steps"
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="1"
          header={
            <Space size={8}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 12 }}>Select Metric</Text>
            </Space>
          }
        >
          <div className="awm-step-body">
            <MetricPicker
              variant="dropdown"
              metrics={metrics}
              selectedMetricId={state.selectedMetricId}
              metricAgg={state.metricAgg}
              activeCategory={state.activeCategory}
              searchQuery={state.searchQuery}
              onSelectMetric={(id) => {
                set("selectedMetricId")(id);
                // One source owns the include-list, so a metric change replaces it,
                // and a count accepts only the total.
                const source = countSourceFromMetricId(id);
                set("countSpec")(source ? defaultCountSpec(source) : undefined);
                // A drill subject is silently dropped for a count, so clear any
                // selection rather than leave one that does nothing.
                if (source && state.selectedDrills.length) set("selectedDrills")([]);
                if (source) set("metricAgg")("sum");
                else if (state.countSpec) set("metricAgg")("avg");
                // Fewer flags, injuries or treatments is better, so the scale runs
                // green -> red for those. A palette the author picked is left alone.
                set("segmentConfig")(segmentConfigForSource(state.segmentConfig, source));
              }}
              onAggChange={(agg) => set("metricAgg")(agg)}
              onCategoryChange={(cat) => set("activeCategory")(cat)}
              onSearchChange={(q) => set("searchQuery")(q)}
              countSlot={state.countSpec && (
                <div className="awm-labeled-row">
                  <Text className="awm-labeled-row-label">Counting</Text>
                  <CountIncludePicker
                    spec={state.countSpec}
                    onChange={(countSpec) => set("countSpec")(countSpec)}
                  />
                </div>
              )}
            />
          </div>
        </Panel>

        <Panel
          key="2"
          header={
            <Space size={8}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 12 }}>Date Range</Text>
            </Space>
          }
        >
          <div className="awm-step-body">
            <DateRangeForm
              allowLatestN
              bindable={isSectionBindable("gauge", "date")}
              value={dateFormValue}
              onChange={(patch: Partial<DateRangeFormFields>) => {
                Object.entries(patch).forEach(([k, v]) => set(k as keyof GaugeFormState)(v));
              }}
            />
          </div>
        </Panel>

        <Panel
          key="3"
          header={
            <Space size={8}>
              <span className="awm-step-number">3</span>
              <Text strong style={{ fontSize: 12 }}>Filters</Text>
            </Space>
          }
        >
          <div className="awm-step-body">
            <PlayersForm
              hideDrills={!!state.countSpec}
              bindable={isSectionBindable("gauge", "subject")}
              value={playersFormValue}
              onChange={(patch: Partial<PlayersFormFields>) => {
                Object.entries(patch).forEach(([k, v]) => set(k as keyof GaugeFormState)(v));
              }}
              getSubjectEntities={getSubjectEntities}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "8px 10px",
                border: "1px solid #e0e5eb",
                borderRadius: 8,
                background: "#fafbfc",
                marginTop: 8,
              }}
            >
              <div>
                <Text strong style={{ fontSize: 12 }}>Exclude Injured Players</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Exclude injured athletes from this value.
                </Text>
              </div>
              <Switch
                size="small"
                checked={state.excludeInjuredPlayers}
                onChange={(v) => set("excludeInjuredPlayers")(v)}
              />
            </div>
          </div>
        </Panel>

        <Panel
          key="4"
          header={
            <Space size={8}>
              <span className="awm-step-number">4</span>
              <Text strong style={{ fontSize: 12 }}>Scale</Text>
            </Space>
          }
        >
          <div className="awm-step-body">
            <GaugeScaleForm
              value={state.scale}
              onChange={set("scale") as (v: GaugeScaleConfig) => void}
              isGpsMetric={isGpsMetric}
              hasSinglePlayer={hasSinglePlayer}
              hasPosition={hasPosition}
              hasBest={hasBest}
            />
          </div>
        </Panel>

        <Panel
          key="5"
          header={
            <Space size={8}>
              <span className="awm-step-number">5</span>
              <Text strong style={{ fontSize: 12 }}>Color Segments</Text>
            </Space>
          }
        >
          <div className="awm-step-body">
            <GaugeSegmentForm
              value={state.segmentConfig}
              onChange={set("segmentConfig") as (v: GaugeSegmentConfig) => void}
            />
          </div>
        </Panel>
      </Collapse>
    </>
  );

  const preview = (
    <GaugePreviewPanel
      title={state.title}
      selectedMetricId={state.selectedMetricId}
      metricAgg={state.metricAgg}
      scale={state.scale}
      segmentConfig={state.segmentConfig}
      metrics={metrics}
    />
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
